import {Classes, Toaster} from '@blueprintjs/core'
import {Snackbar, Dialog} from '@material-ui/core'
import {CatalogContextMenuContext} from '@modules/catalog/CatalogRoot'
import {ui, site, WorkspaceConfig, CatalogStore, StoresInstance} from '@stores'
import {action, computed, observable, runInAction, toJS, trace} from 'mobx'
import {useEffect, _, classObserver, useHotkeys, observer, Fragment, useMemo, useAsObservableSource} from '@api'
import {useQueryClient} from 'react-query'
import * as css from './Dashboard.css'
import {React, classNames, SplitterLayout, Suspense, useLocalStore} from '@api'
import {useStores, VipLocalStore, SiteHeader, TopBar, RightSidebar, CatalogRoot, LeftSidebar, LoadingIndicator, LibraryBoard, UserNotificationSettingsDialog, CatalogContextMenu, MyContextMenu2, EditWorkspacePresetDialog, DevDeploymentsMenu, useVipLocalStore, StoresInstanceContext, DetailsRouteDashboard, CatalogDashboardPage, CatalogDashboardPageStore} from '@design-board/components'

interface MyProps {
  ctx?
  gridRef?: (grid) => void,
  workspace?: WorkspaceConfig
  isDetailsRoute?: boolean
}

/*
 useEffect(() => {
    if (props.workspace && workspaces.current != props.workspace._id) {
      workspaces.current = props.workspace._id
    }
  }, [props.workspace])

 */

export const DashboardComponent = observer<MyProps>(function DashboardComponent({gridRef, workspace, isDetailsRoute}) {
  const {
    contextMenu: {onContextMenuClosed}
  } = useStores()

  return (
    <MyContextMenu2 content={<CatalogContextMenu/>}
      //disabled={sidebar.isShowingModal /* || dragDrop.isDragging */ // Don't do this as it'll cause the whole context menu to close and break when we drag off of the menu
      //}
                    popoverClassName={css.contextMenuPopover}
                    transitionDuration={300}
                    onClosed={onContextMenuClosed}
                    portalClassName={css.contextMenu}
                    {...{hoverCloseDelay: 400} as any}
    >
      {ctx => {
        return <DashboardContent ctx={ctx} gridRef={gridRef} workspace={workspace} isDetailsRoute={isDetailsRoute}/>
      }}
    </MyContextMenu2>)
})

class DashboardContentStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: MyProps) {
    super(stores)
  }

  registerEffects = () => {
    const toDispose = []
    return () => {
      toDispose.forEach(d => d())
    }
  }

  @computed get featureConfig() {
    return this.stores.workspaces.workspaceConfig?.library?.features
  }

  @action onCloseSnackbar = () => {
    const {autoCloseErrorTimer} = site
    autoCloseErrorTimer && clearTimeout(autoCloseErrorTimer)
    site.error = null
  }

  @computed get isInitialLoad() {
    const {workspaces: {deploymentConfig}, stores: {currentUser}} = this
    return _.isEmpty(deploymentConfig) || !currentUser.hasLoadedSettings
  }
}

export const DashboardContentStoreContext = React.createContext<DashboardContentStore>(null)
export const useDashboardContentStore = () => React.useContext(DashboardContentStoreContext)

const DashboardContent = observer<MyProps>(function DashboardContent(props) {
  const obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((ws) => new DashboardContentStore(ws, obsProps))
  const {
    onCloseSnackbar,
    flexLayout: {isMaximized, activeTab}, isInitialLoad,
    props: {ctx, isDetailsRoute}
  } = store,
    queryClient = useQueryClient()

  useEffect(() => {
    site.queryClient = queryClient
  }, [])

  // useEffect(() => {
  //   console.log(`Mount DashboardContent`)
  //   return () => console.log(`Unmount DashboardContent`)
  // }, [])

  useEffect(() => store.registerEffects(), [])

  const storesToInject = (isMaximized ? activeTab?.vipTabStore?.storesToInject : store.stores) ?? store.stores  // Be wary of always using activeCatalogRoot as it'll cause a full repaint on every tab change

  // trace()
  const {
      strings, currentUser, workspaces: {deploymentConfig},
      catalog, contextMenu: {onContextMenu}, sidebar, authStore, site: {onSetToaster, toasts}
    } = storesToInject as StoresInstance

  if (!catalog.initialized) return null

  // console.log(`Dashboard - `, {isMaximized})
  //
  // useEffect(() => {
  //   console.log(`Mount dashboard `)
  //   return () => console.log(`Unmount Dashboard`)
  // }, [])

  // return isDetailsRoute ? <DetailsRouteDashboard/> :

  return (
    <DashboardContentStoreContext.Provider value={store}>
    <CatalogContextMenuContext.Provider value={ctx}>
      <div className={classNames(css.root, ctx.className, Classes.FIXED_POSITIONING_CONTAINING_BLOCK)}
           ref={ctx.ref}
           data-footer={catalog.footer.visible}
           onContextMenu={e => onContextMenu(e, ctx)}
           style={{...catalog?.rootStyle, ...((isMaximized ? sidebar?.styleProperties : {}) ?? {})}}
           >
        {ctx.popover} { /* Context Menu contents (if showing) */}

        {isInitialLoad // || (activeTab?.component === 'catalog' && !flexLayout.catalogRoots.some(cr => cr.hasFinishedFirstLoad))
         ? <Fragment> {
            /* During a load (which can break/stop if the server is dead) we don't have a site header in which to draw our dev menu affordances
          */}
            <LoadingIndicator text={
              <div className={css.loadingIndicatorContents}>
                <span>{_.isEmpty(deploymentConfig) ? strings.loadingWorkspaces : !currentUser.hasLoadedSettings ? 'Loading Settings...' : 'Loading Catalog...'}</span>
                {false && DEBUG && authStore.isLoadingUser && <span>Loading user...</span>}
              </div>
            } />
                <DevDeploymentsMenu/>
         </Fragment> :
         <Fragment>
           <SiteHeader className={css.siteHeader} showUser showPresentation={false}
                       showSearchIcon showBackButton={false}/>

           {isDetailsRoute
            ? <DetailsRouteDashboard />
            : <CatalogDashboardPage storesToInject={storesToInject} />}
         </Fragment>
        }

        <Snackbar
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          onClose={onCloseSnackbar}
          autoHideDuration={ui.settings.AUTOHIDE_SNACKBAR_DURATION}
          open={site.error != null}
          ContentProps={{
            'aria-describedby': 'error-msg'
          }}
          message={<span id='error-msg'>{site?.error?.message ?? 'An error has occurred'}</span>}
        />

        <Toaster position='bottom-right' ref={onSetToaster}>
          {toasts}
        </Toaster>
      </div>
    </CatalogContextMenuContext.Provider>
    </DashboardContentStoreContext.Provider>
  )
})



// WEBPACK FOOTER //
// ./src/modules/dashboard/components/Dashboard.tsx